import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import WhatsApp from "@mui/icons-material/WhatsApp";
import aiDirectCorretorService from "../services/aiDirectCorretor.service";

const CONTACTS_LIMIT = 50;
const MESSAGES_LIMIT = 100;
const DAY_IN_MS = 24 * 60 * 60 * 1000;

const messageText = (message) => {
  const value = message?.texto ?? message?.content ?? message?.message ?? "";
  return typeof value === "string" ? value : value ? JSON.stringify(value) : "";
};

const timestamp = (value) => {
  if (!value) return Number.NaN;
  const normalized = String(value).replace(" ", "T");
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized);
  return new Date(hasTimezone ? normalized : `${normalized}-03:00`).getTime();
};

const formatDate = (value) => {
  const parsed = timestamp(value);
  if (!Number.isFinite(parsed)) return "";
  return new Date(parsed).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const templateBody = (template) =>
  template?.components?.find((component) => String(component?.type).toUpperCase() === "BODY");

const templateVariables = (template) => [
  ...new Set(
    [...String(templateBody(template)?.text || "").matchAll(/{{\s*(\d+)\s*}}/g)].map(
      (match) => match[1]
    )
  ),
];

const renderTemplateText = (template, values, preserveEmpty = false) =>
  String(templateBody(template)?.text || template?.name || "").replace(
    /{{\s*(\d+)\s*}}/g,
    (_, key) => values[key] || (preserveEmpty ? `{{${key}}}` : "")
  );

const sortSituacoes = (items) =>
  [...items].sort((current, next) => {
    const currentOrder = current?.ordem ?? Number.POSITIVE_INFINITY;
    const nextOrder = next?.ordem ?? Number.POSITIVE_INFINITY;
    if (currentOrder !== nextOrder) return currentOrder - nextOrder;
    return Number(current?.idSituacao ?? 0) - Number(next?.idSituacao ?? 0);
  });

const messageKey = (message, index) =>
  message?.id ?? message?.whatsAppMessageId ?? `${message?.dataHora || "message"}-${index}`;

const mergeMessages = (older, current) => {
  const seen = new Set();
  return [...older, ...current].filter((message, index) => {
    const key = messageKey(message, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((currentMessage, nextMessage) => {
    const currentTime = timestamp(currentMessage?.dataHora);
    const nextTime = timestamp(nextMessage?.dataHora);
    if (!Number.isFinite(currentTime) || !Number.isFinite(nextTime)) return 0;
    return currentTime - nextTime;
  });
};

const getContactStatus = (contact, idUsuario) => {
  const attendance = contact?.atendimento;
  if (!attendance?.ativo) return { label: "Disponivel", color: "default" };
  if (String(attendance.produtoOrigem).toUpperCase() === "PREV") return { label: "Em atendimento", color: "warning" };
  if (Number(attendance.idUsuarioResponsavel) === idUsuario) return { label: "Com voce", color: "success" };
  return { label: "Outro responsavel", color: "warning" };
};

const MessageBubble = ({ message, contactName, colors }) => {
  const role = String(message?.role || "").toLowerCase();
  const incoming = role === "user";
  const labels = {
    user: contactName || "Lead",
    assistant: "Agente de Pre-Vendas",
    aidirect: "Atendimento humano",
    corretor: "Corretor",
  };
  const backgrounds = {
    user: colors.incoming,
    assistant: colors.agent,
    aidirect: colors.human,
    corretor: colors.broker,
  };
  const labelColors = {
    assistant: colors.agentLabel,
    aidirect: colors.humanLabel,
    corretor: colors.brokerLabel,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        alignSelf: incoming ? "flex-start" : "flex-end",
        width: "fit-content",
        minWidth: 160,
        maxWidth: { xs: "90%", sm: "80%", md: "72%" },
        p: 1.05,
        pb: 2.25,
        position: "relative",
        borderRadius: incoming ? "4px 8px 8px 8px" : "8px 4px 8px 8px",
        bgcolor: backgrounds[role] || colors.agent,
        color: colors.text,
        overflowWrap: "anywhere",
        boxShadow: "0 1px 1px rgba(0,0,0,0.16)",
      }}
    >
      <Typography variant="caption" fontWeight={800} sx={{ display: "block", mb: 0.25, color: incoming ? colors.incomingLabel : labelColors[role] || colors.outgoingLabel }}>
        {labels[role] || "Mensagem enviada"}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
        {messageText(message)}
      </Typography>
      <Typography
        variant="caption"
        sx={{ position: "absolute", right: 7, bottom: 4, color: colors.messageMeta, fontSize: 10 }}
      >
        {formatDate(message?.dataHora)}
      </Typography>
    </Paper>
  );
};

const TemplateFields = ({ templates, loading, selectedId, values, onSelect, onValueChange, showPreview = true }) => {
  const selected = templates.find((template) => String(template.id) === selectedId) || null;
  return (
    <Stack spacing={1.5}>
      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", minHeight: 80 }}>
          <CircularProgress size={26} />
        </Box>
      ) : (
        <TextField
          select
          fullWidth
          label="Template aprovado"
          value={selectedId}
          onChange={(event) => onSelect(event.target.value)}
          SelectProps={{ native: true }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">Selecione um template</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name} ({template.language})
            </option>
          ))}
        </TextField>
      )}
      {selected && templateVariables(selected).map((key) => (
        <TextField
          key={key}
          fullWidth
          label={`Variavel ${key}`}
          value={values[key] || ""}
          onChange={(event) => onValueChange(key, event.target.value)}
        />
      ))}
      {selected && showPreview && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "#efeae2", color: "#111b21" }}>
          <Typography variant="caption" fontWeight={800}>Previa</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
            {renderTemplateText(selected, values, true)}
          </Typography>
        </Paper>
      )}
    </Stack>
  );
};

const TemplatePreview = ({ template, values }) => (
  <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", height: "100%" }}>
    <Box sx={{ px: 1.25, py: 1, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #d6dadd" }}>
      <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#25d366", display: "grid", placeItems: "center", color: "#fff" }}><WhatsApp sx={{ fontSize: 17 }} /></Box>
      <Box>
        <Typography sx={{ color: "#111b21", fontWeight: 700, fontSize: 12, lineHeight: 1.2 }}>WhatsApp</Typography>
        <Typography sx={{ color: "#667781", fontSize: 10, lineHeight: 1.2 }}>Previa da mensagem</Typography>
      </Box>
    </Box>
    <Box sx={{ bgcolor: "#efeae2", minHeight: 235, p: 1.5, display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
      <Paper elevation={0} sx={{ p: 1.25, borderRadius: "8px 0 8px 8px", bgcolor: "#d9fdd3", color: "#111b21", maxWidth: "94%", boxShadow: "0 1px 1px rgba(0,0,0,0.08)" }}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.45 }}>
          {template ? renderTemplateText(template, values, true) : "Selecione um template para visualizar a mensagem."}
        </Typography>
        <Typography sx={{ mt: 0.5, color: "#667781", textAlign: "right", fontSize: 9 }}>10:42</Typography>
      </Paper>
    </Box>
  </Paper>
);

export default function AiDirectCorretor() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const auth = useMemo(() => {
    try {
      return aiDirectCorretorService.getAuthContext();
    } catch {
      return { idUsuario: null, role: "", readonly: true };
    }
  }, []);

  const [credentials, setCredentials] = useState(null);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [contactSearchRequest, setContactSearchRequest] = useState(0);
  const [hasContactListRequested, setHasContactListRequested] = useState(false);
  const [situacoes, setSituacoes] = useState([]);
  const [loadingSituacoes, setLoadingSituacoes] = useState(false);
  const [selectedSituacaoId, setSelectedSituacaoId] = useState("");
  const [appliedSituacaoId, setAppliedSituacaoId] = useState("");
  const [contactOffset, setContactOffset] = useState(0);
  const [hasNextContacts, setHasNextContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateValues, setTemplateValues] = useState({});
  const [message, setMessage] = useState("");
  const [firstMessage, setFirstMessage] = useState("");
  const [assumeDialogOpen, setAssumeDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  const colors = theme.palette.mode === "dark"
    ? {
        background: "#0b141a",
        sidebar: "#111b21",
        header: "#202c33",
        search: "#202c33",
        border: "#2a3942",
        text: "#e9edef",
        secondary: "#8696a0",
        messageMeta: "#aebac1",
        incoming: "#202c33",
        incomingLabel: "#e9edef",
        outgoingLabel: "#d9fdd3",
        agentLabel: "#d9fdd3",
        humanLabel: "#bfe8ff",
        brokerLabel: "#cde7ff",
        agent: "#005c4b",
        human: "#164b63",
        broker: "#1d5a80",
      }
    : {
        background: "#efeae2",
        sidebar: "#ffffff",
        header: "#f0f2f5",
        search: "#ffffff",
        border: theme.palette.divider,
        text: theme.palette.text.primary,
        secondary: theme.palette.text.secondary,
        messageMeta: "#667781",
        incoming: "#ffffff",
        incomingLabel: "#54656f",
        outgoingLabel: "#0b5e20",
        agentLabel: "#0b5e20",
        humanLabel: "#0b4f71",
        brokerLabel: "#0b3f69",
        agent: "#d9fdd3",
        human: "#dbeeff",
        broker: "#c9e1ff",
      };

  const contactFieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: colors.search,
      borderRadius: 2,
      color: colors.text,
      "& fieldset": { borderColor: colors.border },
      "&:hover fieldset": { borderColor: colors.secondary },
      "&.Mui-focused fieldset": { borderColor: "#00a884" },
    },
    "& .MuiInputLabel-root": { color: colors.secondary },
    "& .MuiSvgIcon-root": { color: colors.secondary },
  };

  const attendance = selectedContact?.atendimento?.ativo ? selectedContact.atendimento : null;
  const attendanceOrigin = String(attendance?.produtoOrigem || "").toUpperCase();
  const selectedContactId = selectedContact?.idLeadCrm;
  const isResponsible = Boolean(attendance) && Number(attendance.idUsuarioResponsavel) === auth.idUsuario;
  const canStart = !attendance && !auth.readonly && auth.role === "corretor";
  const canSend = attendanceOrigin === "CORR" && isResponsible && !auth.readonly && auth.role === "corretor";
  const canClose = canSend;

  const lastUserMessage = useMemo(
    () => [...messages].reverse().find((item) => String(item?.role).toLowerCase() === "user"),
    [messages]
  );
  const withinWindow = useMemo(() => {
    const value = timestamp(lastUserMessage?.dataHora);
    return Number.isFinite(value) && Date.now() - value < DAY_IN_MS;
  }, [lastUserMessage]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => String(template.id) === selectedTemplateId) || null,
    [selectedTemplateId, templates]
  );
  const templateComplete = useMemo(
    () => selectedTemplate && templateVariables(selectedTemplate).every((key) => templateValues[key]?.trim()),
    [selectedTemplate, templateValues]
  );

  const showError = useCallback((error, fallback) => {
    setFeedback({ severity: "error", message: aiDirectCorretorService.getErrorMessage(error, fallback) });
  }, []);

  const loadCredentials = useCallback(async () => {
    setLoadingCredentials(true);
    setFeedback(null);
    try {
      setCredentials(await aiDirectCorretorService.getWhatsappCredentials());
    } catch (error) {
      showError(error, "Nao foi possivel verificar a configuracao do WhatsApp.");
      setCredentials({ configurado: false });
    } finally {
      setLoadingCredentials(false);
    }
  }, [showError]);

  const loadContacts = useCallback(async ({ silent = false, preserveSelection = false } = {}) => {
    if (!credentials?.configurado) return;
    if (!hasContactListRequested) return;
    if (!silent) setLoadingContacts(true);
    try {
      const data = await aiDirectCorretorService.getContacts({
        busca: search,
        idSituacao: appliedSituacaoId || undefined,
        limit: CONTACTS_LIMIT,
        offset: contactOffset,
      });
      setContacts(data);
      setHasNextContacts(data.length === CONTACTS_LIMIT);
      setSelectedContact((current) => {
        if (!current) return null;
        const updated = data.find((contact) => Number(contact.idLeadCrm) === Number(current.idLeadCrm));
        return updated || (preserveSelection ? current : null);
      });
    } catch (error) {
      showError(error, "Nao foi possivel carregar os seus leads.");
    } finally {
      if (!silent) setLoadingContacts(false);
    }
  }, [appliedSituacaoId, contactOffset, credentials?.configurado, hasContactListRequested, search, showError]);

  const loadSituacoes = useCallback(async () => {
    if (!credentials?.configurado) return;
    setLoadingSituacoes(true);
    try {
      const data = await aiDirectCorretorService.getSituacoes();
      setSituacoes(sortSituacoes(data));
    } catch (error) {
      showError(error, "Nao foi possivel carregar as situacoes do CVCRM.");
    } finally {
      setLoadingSituacoes(false);
    }
  }, [credentials?.configurado, showError]);

  const loadMessages = useCallback(async (contact, { silent = false, offset = 0, prepend = false } = {}) => {
    if (!contact?.idLeadCrm) return;
    if (prepend) setLoadingOlder(true);
    else if (!silent) setLoadingMessages(true);

    const container = messagesContainerRef.current;
    const previousHeight = prepend ? container?.scrollHeight || 0 : 0;
    try {
      const data = await aiDirectCorretorService.getMessages({
        idLeadCrm: contact.idLeadCrm,
        limit: MESSAGES_LIMIT,
        offset,
      });
      setMessages((current) => {
        if (prepend) return mergeMessages(data, current);
        if (silent) return mergeMessages(current, data);
        return mergeMessages([], data);
      });
      if (!silent) {
        setMessagesOffset(offset);
        setHasOlderMessages(data.length === MESSAGES_LIMIT);
      }

      requestAnimationFrame(() => {
        if (prepend && container) container.scrollTop = container.scrollHeight - previousHeight;
        else if (!silent) messagesEndRef.current?.scrollIntoView({ block: "end" });
      });
    } catch (error) {
      if (!silent) showError(error, "Nao foi possivel carregar o historico deste lead.");
    } finally {
      if (prepend) setLoadingOlder(false);
      else if (!silent) setLoadingMessages(false);
    }
  }, [showError]);

  const loadTemplates = useCallback(async () => {
    if (templates.length) return;
    setLoadingTemplates(true);
    try {
      const response = await aiDirectCorretorService.getTemplates({ limit: 50 });
      setTemplates((response.data || []).filter((template) => template.status === "APPROVED"));
    } catch (error) {
      showError(error, "Nao foi possivel carregar os templates aprovados.");
    } finally {
      setLoadingTemplates(false);
    }
  }, [showError, templates.length]);

  useEffect(() => { loadCredentials(); }, [loadCredentials]);
  useEffect(() => { loadSituacoes(); }, [loadSituacoes]);
  useEffect(() => { loadContacts(); }, [contactSearchRequest, loadContacts]);
  useEffect(() => {
    setMessages([]);
    setMessagesOffset(0);
    setHasOlderMessages(false);
    if (selectedContactId) loadMessages({ idLeadCrm: selectedContactId });
  }, [loadMessages, selectedContactId]);
  useEffect(() => {
    if (!selectedContact) return undefined;
    const interval = window.setInterval(() => {
      if (document.hidden) return;
      loadMessages(selectedContact, { silent: true });
      loadContacts({ silent: true, preserveSelection: true });
    }, 20000);
    return () => window.clearInterval(interval);
  }, [loadContacts, loadMessages, selectedContact]);

  const resetTemplate = () => {
    setSelectedTemplateId("");
    setTemplateValues({});
  };

  const handleContactSearch = () => {
    setSearch(searchInput.trim());
    setAppliedSituacaoId(selectedSituacaoId);
    setContactOffset(0);
    setSelectedContact(null);
    setHasContactListRequested(true);
    setContactSearchRequest((current) => current + 1);
  };

  const openAssumeDialog = async () => {
    setFeedback(null);
    setFirstMessage("");
    resetTemplate();
    setAssumeDialogOpen(true);
    if (!withinWindow) await loadTemplates();
  };

  const openTemplateDialog = async () => {
    setFeedback(null);
    resetTemplate();
    setTemplateDialogOpen(true);
    await loadTemplates();
  };

  const buildTemplatePayload = () => {
    const variables = templateVariables(selectedTemplate);
    const payload = {
      nome: selectedTemplate.name,
      idioma: selectedTemplate.language,
      texto: renderTemplateText(selectedTemplate, templateValues),
      componentes: variables.length
        ? [{ type: "body", parameters: variables.map((key) => ({ type: "text", text: templateValues[key] })) }]
        : [],
    };
    return payload;
  };

  const refreshAfterAction = async () => {
    await Promise.all([
      loadMessages(selectedContact),
      loadContacts({ silent: true, preserveSelection: true }),
    ]);
  };

  const handleAssumeAndSend = async () => {
    if (!selectedContact || submitting || (withinWindow ? !firstMessage.trim() : !templateComplete)) return;
    setSubmitting(true);
    try {
      const payload = withinWindow
        ? { idLeadCrm: selectedContact.idLeadCrm, tipoMensagem: "text", texto: firstMessage.trim() }
        : { idLeadCrm: selectedContact.idLeadCrm, tipoMensagem: "template", ...buildTemplatePayload() };
      const response = await aiDirectCorretorService.assumeAndSend(payload);
      setSelectedContact((current) => current ? { ...current, atendimento: response.atendimento } : current);
      setAssumeDialogOpen(false);
      setFirstMessage("");
      resetTemplate();
      await refreshAfterAction();
    } catch (error) {
      showError(error, "Nao foi possivel assumir e enviar a mensagem.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!canSend || !withinWindow || !message.trim() || submitting) return;
    setSubmitting(true);
    try {
      await aiDirectCorretorService.sendMessage({
        idAtendimento: attendance.idExterno,
        texto: message.trim(),
      });
      setMessage("");
      await refreshAfterAction();
    } catch (error) {
      showError(error, "Nao foi possivel enviar a mensagem.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!canSend || !templateComplete || submitting) return;
    setSubmitting(true);
    try {
      await aiDirectCorretorService.sendTemplate({
        idAtendimento: attendance.idExterno,
        ...buildTemplatePayload(),
      });
      setTemplateDialogOpen(false);
      resetTemplate();
      await refreshAfterAction();
    } catch (error) {
      showError(error, "Nao foi possivel enviar o template.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAttendance = async () => {
    if (!canClose || submitting) return;
    setSubmitting(true);
    try {
      await aiDirectCorretorService.closeAttendance(attendance.idExterno);
      setCloseDialogOpen(false);
      setSelectedContact((current) => current ? { ...current, atendimento: null } : current);
      await refreshAfterAction();
      setFeedback({ severity: "success", message: "Atendimento encerrado." });
    } catch (error) {
      showError(error, "Nao foi possivel encerrar o atendimento.");
    } finally {
      setSubmitting(false);
    }
  };

  const attendanceMessage = attendanceOrigin === "PREV"
    ? `Este lead ja esta em atendimento de Pre-Vendas${attendance?.nomeUsuarioResponsavel ? ` com ${attendance.nomeUsuarioResponsavel}` : ""}.`
    : attendance && !isResponsible
      ? `Atendimento em andamento com ${attendance.nomeUsuarioResponsavel || "outro responsavel"}.`
      : "";

  if (loadingCredentials) {
    return <Box sx={{ minHeight: 520, display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  if (!credentials?.configurado) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center", p: 3 }}>
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 520, textAlign: "center" }}>
          <WhatsApp sx={{ fontSize: 48, color: "text.secondary" }} />
          <Typography variant="h6" fontWeight={900}>WhatsApp nao configurado</Typography>
          <Typography color="text.secondary">
            O WhatsApp do Agente de Pre-Vendas ainda nao esta disponivel para esta operacao.
          </Typography>
          {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}
          <Button variant="outlined" startIcon={<RefreshRounded />} onClick={loadCredentials}>Verificar novamente</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "clamp(420px, calc(100dvh - 205px), 680px)", md: "calc(100vh - 260px)" },
        minHeight: { md: 600 },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "330px minmax(0, 1fr)" },
        overflow: "hidden",
        border: "1px solid",
        borderColor: colors.border,
        borderRadius: { xs: 0, sm: 1 },
        bgcolor: colors.sidebar,
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          display: { xs: selectedContact ? "none" : "flex", md: "flex" },
          flexDirection: "column",
          borderRight: { md: "1px solid" },
          borderColor: colors.border,
          bgcolor: colors.sidebar,
          overflow: "hidden",
        }}
      >
        <Stack spacing={1.2} sx={{ p: 1.25, bgcolor: colors.header, borderBottom: "1px solid", borderColor: colors.border }}>
          <TextField
            size="small"
            fullWidth
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleContactSearch();
              }
            }}
            placeholder="Buscar lead"
            sx={contactFieldSx}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment>,
            }}
          />
          <Stack direction="row" spacing={0.75} alignItems="center">
            <TextField
              select
              fullWidth
              size="small"
              label="Situacao"
              value={selectedSituacaoId}
              onChange={(event) => {
                setSelectedSituacaoId(event.target.value);
                setContactOffset(0);
                setSelectedContact(null);
                setContacts([]);
                setHasContactListRequested(false);
              }}
              disabled={loadingSituacoes}
              sx={contactFieldSx}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">Todas as situacoes</option>
              {situacoes.map((situacao) => (
                <option key={situacao.idSituacao} value={situacao.idSituacao}>
                  {situacao.nome}
                </option>
              ))}
            </TextField>
            <Tooltip title="Buscar leads">
              <span>
                <IconButton
                  aria-label="Buscar leads"
                  onClick={handleContactSearch}
                  disabled={loadingContacts || loadingSituacoes}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "primary.dark" },
                    "&.Mui-disabled": { bgcolor: "action.disabledBackground", color: "action.disabled" },
                  }}
                >
                  <SearchRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {!selectedContact && feedback && (
            <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert>
          )}
        </Stack>

        {loadingContacts ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}><CircularProgress size={28} /></Box>
        ) : (
          <List disablePadding sx={{ flex: 1, overflowY: "auto", bgcolor: colors.sidebar }}>
            {contacts.map((contact) => {
              const status = getContactStatus(contact, auth.idUsuario);
              const selected = Number(selectedContact?.idLeadCrm) === Number(contact.idLeadCrm);
              return (
                <ListItemButton
                  key={contact.idLeadCrm}
                  selected={selected}
                  onClick={() => setSelectedContact(contact)}
                  sx={{
                    alignItems: "flex-start",
                    gap: 1,
                    py: 1.25,
                    px: 1.5,
                    borderBottom: "1px solid",
                    borderColor: colors.border,
                    color: colors.text,
                    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: theme.palette.mode === "dark" ? "#2a3942" : "#e9edef" },
                    "&:hover": { bgcolor: theme.palette.mode === "dark" ? "#18252c" : "#f5f6f6" },
                  }}
                >
                  <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.mode === "dark" ? "#6a7175" : "#b7bdc1", color: "#fff" }}>{String(contact.nome || "L").charAt(0).toUpperCase()}</Avatar>
                  <ListItemText
                    sx={{ minWidth: 0, m: 0 }}
                    primary={
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" fontWeight={800} noWrap>{contact.nome || "Lead sem nome"}</Typography>
                        <Typography variant="caption" sx={{ flexShrink: 0, color: colors.secondary }}>
                          {formatDate(contact.dataHoraUltimaMensagem)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Box component="span" sx={{ display: "block", mt: 0.4 }}>
                        <Typography component="span" variant="caption" noWrap sx={{ display: "block", color: colors.secondary }}>
                          {messageText(contact.ultimaMensagem) || contact.whatsAppId}
                        </Typography>
                        <Stack component="span" direction="row" alignItems="center" spacing={0.55} sx={{ mt: 0.55 }}>
                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: status.color === "success" ? "#25d366" : status.color === "warning" ? "#f5a524" : colors.secondary }} />
                          <Typography component="span" variant="caption" sx={{ color: colors.secondary }}>{status.label}</Typography>
                        </Stack>
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
            {!contacts.length && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography fontWeight={800} sx={{ color: colors.text }}>
                  {hasContactListRequested ? "Nenhum lead encontrado" : "Selecione um filtro para listar leads"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: colors.secondary }}>
                  {hasContactListRequested
                    ? "Tente alterar a busca ou a situacao."
                    : "Escolha uma situacao ou digite uma busca e clique na lupa."}
                </Typography>
              </Box>
            )}
          </List>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.25, py: 0.8, bgcolor: colors.header, borderTop: "1px solid", borderColor: colors.border }}>
          <Tooltip title="Pagina anterior">
            <span><IconButton size="small" disabled={loadingContacts || contactOffset === 0} onClick={() => setContactOffset(Math.max(0, contactOffset - CONTACTS_LIMIT))}><ChevronLeftRounded /></IconButton></span>
          </Tooltip>
          <Typography variant="caption" sx={{ color: colors.secondary }}>Pagina {Math.floor(contactOffset / CONTACTS_LIMIT) + 1}</Typography>
          <Tooltip title="Proxima pagina">
            <span><IconButton size="small" disabled={loadingContacts || !hasNextContacts} onClick={() => setContactOffset(contactOffset + CONTACTS_LIMIT)}><ChevronRightRounded /></IconButton></span>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ minWidth: 0, display: { xs: selectedContact ? "flex" : "none", md: "flex" }, flexDirection: "column", overflow: "hidden", bgcolor: colors.background }}>
        {!selectedContact ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3, textAlign: "center" }}>
            <Stack spacing={1} alignItems="center"><WhatsApp color="disabled" sx={{ fontSize: 44 }} /><Typography color="text.secondary">Selecione um lead para ver a conversa.</Typography></Stack>
          </Box>
        ) : (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 1.25, py: 0.85, bgcolor: colors.header, borderBottom: "1px solid", borderColor: colors.border }}>
              <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
                {isMobile && <IconButton aria-label="Voltar para os leads" onClick={() => setSelectedContact(null)}><ArrowBackRounded /></IconButton>}
                <Box minWidth={0}>
                  <Typography fontWeight={800} noWrap sx={{ color: colors.text }}>{selectedContact.nome || "Lead sem nome"}</Typography>
                  <Typography variant="caption" sx={{ color: colors.secondary }}>{selectedContact.whatsAppId}</Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.8}>
                {canStart && <Button size="small" variant="contained" onClick={openAssumeDialog} sx={{ whiteSpace: "nowrap", bgcolor: "#00a884", "&:hover": { bgcolor: "#008f72" } }}>Assumir conversa</Button>}
                <Tooltip title="Atualizar conversa">
                  <IconButton onClick={() => { loadMessages(selectedContact); loadContacts({ silent: true, preserveSelection: true }); }}><RefreshRounded /></IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {attendance && (
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={0.8} sx={{ px: 1.5, py: 0.8, bgcolor: colors.header, borderBottom: "1px solid", borderColor: colors.border }}>
                <Typography variant="body2" color={attendanceMessage ? "warning.main" : "text.secondary"}>
                  {attendanceMessage || <>Atendimento sob sua responsabilidade.</>}
                </Typography>
                {canClose && <Button size="small" color="error" variant="outlined" startIcon={<CloseRounded />} onClick={() => { setFeedback(null); setCloseDialogOpen(true); }}>Encerrar</Button>}
              </Stack>
            )}
            {feedback && <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ borderRadius: 0 }}>{feedback.message}</Alert>}

            <Box ref={messagesContainerRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: { xs: 1, sm: 1.5 }, display: "flex", flexDirection: "column", gap: 0.85, bgcolor: colors.background }}>
              {hasOlderMessages && (
                <Button size="small" variant="text" disabled={loadingOlder} onClick={() => loadMessages(selectedContact, { offset: messagesOffset + MESSAGES_LIMIT, prepend: true })} sx={{ alignSelf: "center" }}>
                  {loadingOlder ? "Carregando..." : "Carregar mensagens anteriores"}
                </Button>
              )}
              {loadingMessages ? (
                <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}><CircularProgress size={28} /></Box>
              ) : messages.length ? (
                messages.map((item, index) => <MessageBubble key={messageKey(item, index)} message={item} contactName={selectedContact.nome} colors={colors} />)
              ) : (
                <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}><Typography color="text.secondary">Ainda nao ha mensagens com este lead.</Typography></Box>
              )}
              <Box ref={messagesEndRef} />
            </Box>

            {attendanceMessage && <Alert severity="warning" sx={{ borderRadius: 0 }}>{attendanceMessage}</Alert>}
            {!attendance && !canStart && <Alert severity="info" sx={{ borderRadius: 0 }}>O envio nao esta disponivel para este usuario.</Alert>}
            {canSend && !withinWindow && <Alert severity="info" sx={{ borderRadius: 0 }}>A janela de 24 horas terminou. Envie um template aprovado.</Alert>}
            <Stack direction="row" spacing={0.8} alignItems="flex-end" sx={{ p: { xs: 0.75, sm: 1 }, bgcolor: colors.header, borderTop: "1px solid", borderColor: colors.border }}>
              <Tooltip title="Enviar template">
                <span><IconButton aria-label="Enviar template" disabled={!canSend || submitting} onClick={openTemplateDialog}><DescriptionOutlined /></IconButton></span>
              </Tooltip>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={1}
                maxRows={4}
                placeholder={canSend ? (withinWindow ? "Digite uma mensagem" : "Use um template aprovado") : canStart ? "Assuma a conversa para enviar a primeira mensagem" : "Envio indisponivel"}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                disabled={!canSend || !withinWindow || submitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: colors.search,
                    borderRadius: 3,
                    color: colors.text,
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input::placeholder": { color: colors.secondary, opacity: 1 },
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <Tooltip title="Enviar mensagem">
                <span><IconButton aria-label="Enviar mensagem" disabled={!canSend || !withinWindow || !message.trim() || submitting} onClick={handleSendMessage} sx={{ color: "#00a884", "&.Mui-disabled": { color: colors.secondary } }}><SendRounded /></IconButton></span>
              </Tooltip>
            </Stack>
          </>
        )}
      </Box>

      <Dialog open={assumeDialogOpen} onClose={() => !submitting && setAssumeDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Assumir conversa</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            O atendimento sera criado somente depois que a primeira mensagem for enviada.
          </Typography>
          {feedback?.severity === "error" && <Alert severity="error" sx={{ mb: 2 }}>{feedback.message}</Alert>}
          {!withinWindow && <Alert severity="info" sx={{ mb: 2 }}>Nao ha mensagem recente do lead. O primeiro envio deve usar um template aprovado.</Alert>}
          {withinWindow ? (
            <TextField autoFocus fullWidth multiline minRows={3} label="Primeira mensagem" value={firstMessage} onChange={(event) => setFirstMessage(event.target.value)} />
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(280px, 0.9fr)" }, gap: 2 }}>
              <TemplateFields templates={templates} loading={loadingTemplates} selectedId={selectedTemplateId} values={templateValues} onSelect={(value) => { setSelectedTemplateId(value); setTemplateValues({}); }} onValueChange={(key, value) => setTemplateValues((current) => ({ ...current, [key]: value }))} showPreview={false} />
              <TemplatePreview template={selectedTemplate} values={templateValues} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssumeDialogOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" onClick={handleAssumeAndSend} disabled={submitting || (withinWindow ? !firstMessage.trim() : !templateComplete)}>
            {submitting ? "Enviando..." : "Assumir e enviar mensagem"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templateDialogOpen} onClose={() => !submitting && setTemplateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Enviar template</DialogTitle>
        <DialogContent>
          {feedback?.severity === "error" && <Alert severity="error" sx={{ mb: 2 }}>{feedback.message}</Alert>}
          <TemplateFields templates={templates} loading={loadingTemplates} selectedId={selectedTemplateId} values={templateValues} onSelect={(value) => { setSelectedTemplateId(value); setTemplateValues({}); }} onValueChange={(key, value) => setTemplateValues((current) => ({ ...current, [key]: value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" onClick={handleSendTemplate} disabled={submitting || !templateComplete}>{submitting ? "Enviando..." : "Enviar template"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeDialogOpen} onClose={() => !submitting && setCloseDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Encerrar atendimento?</DialogTitle>
        <DialogContent>
          {feedback?.severity === "error" && <Alert severity="error" sx={{ mb: 2 }}>{feedback.message}</Alert>}
          <Typography color="text.secondary">O atendimento nao podera ser reaberto. Um proximo contato criara um novo atendimento.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)} disabled={submitting}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleCloseAttendance} disabled={submitting}>{submitting ? "Encerrando..." : "Encerrar"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
